import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LayoutDashboard, Sparkles, Palette, Images, CreditCard, Settings, LogOut, Menu, X, Zap, Users, Layers, Shield, FlaskConical, Send } from 'lucide-react';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/studio', label: 'Studio', icon: Sparkles },
  { path: '/brand-kits', label: 'Brand Kits', icon: Palette },
  { path: '/gallery', label: 'Gallery', icon: Images },
  { path: '/distribute', label: 'Distribute', icon: Send },
  { path: '/ab-testing', label: 'A/B Testing', icon: FlaskConical },
  { path: '/batch', label: 'Batch', icon: Layers },
  { path: '/teams', label: 'Teams', icon: Users },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { path: '/settings', label: 'Settings', icon: Settings },
  { path: '/admin', label: 'Admin', icon: Shield },
];

export const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#08080A] flex">
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 bg-[#0A0A0C] border-r border-white/5 transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`} data-testid="sidebar">
        <div className="h-16 flex items-center px-5 border-b border-white/5">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <Sparkles className="w-5 h-5 text-[#CDFE65]" />
            <span className="font-black text-lg tracking-tight" style={{ fontFamily: 'Chivo' }}>THE HUMAN</span>
          </Link>
        </div>
        <nav className="p-3 space-y-0.5">
          {NAV_ITEMS.map(item => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${active ? 'bg-[#CDFE65]/10 text-[#CDFE65] font-medium' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
                data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
              >
                <item.icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-white/5">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#CDFE65]/20 flex items-center justify-center text-[#CDFE65] text-xs font-bold">
              {(user?.name || user?.email || '?')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name || 'User'}</p>
              <p className="text-[10px] text-white/30 font-mono truncate">{user?.email}</p>
            </div>
          </div>
          <button onClick={logout} className="flex items-center gap-3 px-3 py-2 text-sm text-white/30 hover:text-red-400 transition-colors w-full rounded-lg hover:bg-white/5" data-testid="sidebar-logout">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 lg:ml-56">
        {/* Top bar */}
        <header className="h-16 border-b border-white/5 flex items-center justify-between px-6 glass sticky top-0 z-20" data-testid="topbar">
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-white/60 hover:text-white" data-testid="menu-toggle">
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs font-mono text-white/30">
              <Zap className="w-3.5 h-3.5 text-[#CDFE65]" />
              <span>{user?.generations_used || 0}/{user?.generations_limit || 10}</span>
            </div>
            <Link to="/studio" className="btn-primary text-xs py-2 px-5" data-testid="topbar-studio-btn">
              Launch Studio
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AppLayout;
